import { useMemo } from 'react';
import {
  hasEmptySurveyPlaceholder,
  clearSurveyPlaceholderError,
} from '@/utils/editor/sanitize-template-variables';
import { useShowToast } from './useShowToast';
import { isEmailBodyEmpty, toVariablesList } from '@/utils/emailComposition';
import { replaceSurveySpanWithPlaceholder } from '@/utils/surveyFormatter';
import { checkForInvalidVariablesInSubjectAndBody } from '@/utils/commonHelpers';
import { filterSubjectEmailVariables } from '@/utils/filterEmailVariables';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { REJECTION_EMAIL_VARIABLES_VIEW } from '@/formConfigs/useRejectEmailFormConfig/rejectEmailVariables';

export const useRejection = () => {
  const toast = useShowToast();

  // Same query, same `view`, as the composer's insert picker
  // (`RejectEmailComposer`) — RTK Query dedupes the request, and both sides
  // read off one cache entry so the picker's allowlist and this check can
  // never drift apart.
  const { data: { variables = {} } = {} } = useGetTemplatesVariablesQuery({
    params: { view: REJECTION_EMAIL_VARIABLES_VIEW },
  });
  const variablesListArr = useMemo(
    () => toVariablesList(variables),
    [variables]
  );
  // The signature variable is body-only — a subject line has no "signature".
  const variablesListSubject = useMemo(
    () => toVariablesList(filterSubjectEmailVariables(variables)),
    [variables]
  );

  const validateRejectionEmailPayload = (sendRejectEmail, values: any) => {
    const rejectEmailConfig = values?.rejectEmailConfig || {};
    const subject = rejectEmailConfig?.subject || values?.subject;

    if (sendRejectEmail) {
      const from = rejectEmailConfig?.from || values?.from;
      const body = rejectEmailConfig?.body || values?.body;

      if (!from) {
        toast({ title: 'From is mandatory', status: 'error' });
        return false;
      }
      if (!subject) {
        toast({ title: 'Subject is mandatory', status: 'error' });
        return false;
      }
      if (isEmailBodyEmpty(body)) {
        toast({ title: 'Body is mandatory', status: 'error' });
        return false;
      }

      // `{{survey.link}}` in the body is the wire format, not a sign that
      // nothing was picked: `replaceSurveySpanWithPlaceholder` turns the chosen
      // survey's span back into that token on the way out, and the id travels
      // separately as `survey[ats_form_template_id]`. Reading the body alone
      // therefore rejected sends for a survey that was visibly selected and
      // already stored, which is what customers hit (ENG-3826).
      const selectedSurvey = rejectEmailConfig?.survey ?? values?.survey;
      const hasSelectedSurvey = Boolean(
        selectedSurvey?.value ?? selectedSurvey?.id
      );

      // Check the body the SERVER sees: the payload converts every survey
      // node to `{{survey.link}}`, so reading raw HTML let a node with a stale
      // `surveyid` pass while the form held no survey (CORE-5230).
      const wireBody = replaceSurveySpanWithPlaceholder(body);

      if (hasSelectedSurvey) {
        clearSurveyPlaceholderError();
      } else if (hasEmptySurveyPlaceholder(wireBody)) {
        toast({ title: 'Please select a survey', status: 'error' });
        return false;
      }

      // Skip until the variables list has loaded — with an empty list every
      // `{{variable}}` would count as invalid and wrongly block the send.
      // Checks `wireBody`, and allows `survey.link` alongside the merge
      // variables: it's a real, already-validated token from a different
      // system (the survey node above), not an unknown one.
      if (
        variablesListArr.length > 0 &&
        checkForInvalidVariablesInSubjectAndBody({
          variablesListArr: [...variablesListArr, 'survey.link'],
          variablesListSubject,
          subject,
          body: wireBody,
        })
      ) {
        toast({
          title: 'Invalid variables detected',
          status: 'error',
          description: 'Remove or fix them before sending.',
        });
        return false;
      }
    }
    return true;
  };

  return {
    validateRejectionEmailPayload,
  };
};
